import {
  fetchRoleBasedProjects,
  normalizeProfileId,
  normalizeUserRole,
} from '@/lib/fetchRoleBasedProjects';

export interface SidebarProjectRow {
  id: string;
  project_name: string;
}

const SIDEBAR_COLUMNS = 'id, project_name';
const CACHE_TTL_MS = 5 * 60 * 1000;
const BACKGROUND_REFRESH_DEFER_MS = 300;

type CacheEntry = {
  projects: SidebarProjectRow[];
  timestamp: number;
};

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<SidebarFetchResult>>();

export type SidebarFetchResult = {
  projects: SidebarProjectRow[];
  error: string | null;
};

function cacheKey(profileId: string, role: string): string {
  return `${role}:${profileId}`;
}

function mapRows(rows: Record<string, unknown>[]): SidebarProjectRow[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const id = normalizeProfileId(row.id);
      if (!id) return null;
      const name = row.project_name;
      return {
        id,
        project_name:
          typeof name === 'string' && name.trim() ? name.trim() : 'Untitled Project',
      };
    })
    .filter((row): row is SidebarProjectRow => row !== null);
}

export function getCachedSidebarProjects(
  profileId: string,
  role: string
): SidebarProjectRow[] | null {
  const entry = cache.get(cacheKey(profileId, role));
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
  return entry.projects.length > 0 ? entry.projects : null;
}

export function primeSidebarProjectsCache(
  profileId: string,
  role: string,
  projects: SidebarProjectRow[]
): void {
  if (!Array.isArray(projects) || projects.length === 0) return;
  cache.set(cacheKey(profileId, role), { projects, timestamp: Date.now() });
}

export function mergeSidebarProjectsCache(
  profileId: string,
  role: string,
  extra: SidebarProjectRow[]
): SidebarProjectRow[] {
  if (!Array.isArray(extra) || extra.length === 0) {
    return getCachedSidebarProjects(profileId, role) ?? [];
  }
  const key = cacheKey(profileId, role);
  const existing = cache.get(key)?.projects ?? getCachedSidebarProjects(profileId, role) ?? [];
  const byId = new Map<string, SidebarProjectRow>();
  for (const row of [...existing, ...extra]) {
    if (row?.id) byId.set(row.id, row);
  }
  const merged = Array.from(byId.values());
  if (merged.length > 0) {
    cache.set(key, { projects: merged, timestamp: Date.now() });
  }
  return merged;
}

function scheduleBackgroundRefresh(run: () => void): void {
  if (typeof window === 'undefined') return;
  window.setTimeout(run, BACKGROUND_REFRESH_DEFER_MS);
}

async function runSidebarFetch(
  profileId: string,
  role: string,
  staleFallback: SidebarProjectRow[] | undefined
): Promise<SidebarFetchResult> {
  try {
    const result = await fetchRoleBasedProjects({
      columns: SIDEBAR_COLUMNS,
      profileId,
      role,
    });

    if (result.error) {
      console.error('[sidebarProjectsCache] load failed', {
        error: result.error,
        role,
        profileId,
        table: 'jphph_projects',
      });
      return {
        projects: staleFallback ?? [],
        error: result.error,
      };
    }

    const projects = mapRows(result.rows);
    console.log('[sidebarProjectsCache] loaded projects', {
      count: projects.length,
      role,
      profileId,
      table: 'jphph_projects',
    });

    if (projects.length > 0) {
      cache.set(cacheKey(profileId, role), { projects, timestamp: Date.now() });
      return { projects, error: null };
    }

    if (role === 'management' && (result.rows?.length ?? 0) === 0) {
      const message =
        'Projects query returned 0 rows for management user. Check auth session and RLS on jphph_projects.';
      console.warn('[sidebarProjectsCache]', message, { profileId, role });
      return {
        projects: staleFallback ?? [],
        error: staleFallback?.length ? null : message,
      };
    }

    return { projects, error: null };
  } catch (err) {
    console.error('[sidebarProjectsCache] unexpected error', err);
    return {
      projects: staleFallback ?? [],
      error: err instanceof Error ? err.message : 'Unexpected sidebar project load error',
    };
  }
}

/** Lightweight sidebar project list — cached per session, deduped in-flight, always resolves. */
export function fetchSidebarProjectsLite(
  profileIdInput: string,
  roleInput: string,
  options?: { backgroundRefresh?: boolean; forceRefresh?: boolean }
): Promise<SidebarFetchResult> {
  const profileId = normalizeProfileId(profileIdInput);
  const role = normalizeUserRole(roleInput);
  if (!profileId || !role) {
    const message = 'Missing profile id or role for sidebar project fetch';
    console.warn('[sidebarProjectsCache]', message, { profileIdInput, roleInput });
    return Promise.resolve({ projects: [], error: message });
  }

  const key = cacheKey(profileId, role);
  const cached = cache.get(key);
  const staleFallback = cached?.projects?.length ? cached.projects : undefined;
  const hasFreshCache =
    cached &&
    cached.projects.length > 0 &&
    Date.now() - cached.timestamp < CACHE_TTL_MS;

  if (hasFreshCache && !options?.backgroundRefresh && !options?.forceRefresh) {
    return Promise.resolve({ projects: cached.projects, error: null });
  }

  const existing = inflight.get(key);
  if (existing) return existing;

  const startFetch = () => runSidebarFetch(profileId, role, staleFallback);

  const promise =
    hasFreshCache && options?.backgroundRefresh
      ? new Promise<SidebarFetchResult>((resolve) => {
          scheduleBackgroundRefresh(() => {
            void startFetch().then(resolve).catch((err) =>
              resolve({
                projects: staleFallback ?? [],
                error: err instanceof Error ? err.message : 'Sidebar refresh failed',
              })
            );
          });
        })
      : startFetch();

  inflight.set(
    key,
    promise.finally(() => {
      inflight.delete(key);
    })
  );

  return promise;
}
